import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Store, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { SECTIONS } from "@/components/admin/schemas";
import { AdminForm } from "@/components/admin/AdminForm";

const PUBLIC_FIELDS = new Set(["name", "category", "address", "suburb", "description", "image", "gallery", "price_range", "phone", "website", "instagram", "hours", "tags"]);

export default function SubmitListing() {
  const { token } = useParams();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get(`/invites/${token}`).then((r) => setInvite(r.data)).catch((e) => setError(e.response?.data?.detail || "This link isn't valid."));
  }, [token]);

  const submitFn = (form) => api.post(`/invites/${token}/submit`, form);
  const section = { ...SECTIONS.businesses, singular: "listing", fields: SECTIONS.businesses.fields.filter((f) => PUBLIC_FIELDS.has(f.key)) };

  if (error) return <div className="min-h-[60vh] grid place-items-center px-6 text-center" data-testid="submit-invalid"><div><h2 className="font-display text-4xl">Hmm, that link isn't valid.</h2><p className="mt-3 text-[color:var(--ink-2)]">{error} Ask the Hey GC team for a fresh invitation.</p></div></div>;
  if (done) return (
    <div className="min-h-[60vh] grid place-items-center px-6 text-center" data-testid="submit-success">
      <div><CheckCircle2 size={40} className="mx-auto text-emerald-600" /><h2 className="font-display text-4xl mt-4">Thanks — you're in the queue.</h2><p className="mt-3 text-[color:var(--ink-2)] max-w-md">{done}</p></div>
    </div>
  );
  if (!invite) return <div className="min-h-[60vh] grid place-items-center text-[color:var(--ink-2)]">Loading…</div>;

  return (
    <div className="max-w-[900px] mx-auto px-6 lg:px-10 pt-12 pb-24" data-testid="submit-listing-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.2em] font-bold text-[color:var(--coral)]"><Store size={14} /> By invitation</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-3 tracking-tight">Tell the coast about {invite.business_name || "your business"}.</h1>
        <p className="text-[color:var(--ink-2)] mt-3 max-w-2xl">Fill in your listing below. The Hey GC team will review it before it goes live in Tried & Tested.{invite.note ? ` ${invite.note}` : ""}</p>
      </motion.div>
      <div className="mt-10 bg-white rounded-3xl border border-[color:var(--line)] p-6 md:p-8">
        <AdminForm
          section={section}
          sectionKey="businesses"
          item={{ name: invite.business_name, email: invite.contact_email }}
          uploadPath={`/invites/${token}/upload`}
          submitFn={submitFn}
          aiEnabled={false}
          submitLabel="Submit for review"
          onSaved={(d) => setDone(d.message)}
          onCancel={() => window.history.back()}
        />
      </div>
    </div>
  );
}
